/**
 * @module services/appointment-observation
 * @description UC-REG-275 — Observaciones de turno. Viven en el API en
 *              `/appointment-observations` (colección propia, nunca dentro
 *              del turno). El staff lee todas las de su organización y las
 *              crea / edita (texto vacío = borrar); el paciente solo recibe
 *              las de sus turnos marcadas como visibles.
 */
import api from "./api";

const endpoint = "/appointment-observations";

/** @description Largo máximo del texto (mismo `maxlength` que el API). */
export const OBSERVATION_MAX_LENGTH = 500;

/** @description Opciones para no mostrar toasts de error (lecturas). */
const SILENT = { getErrorMessage: () => null };

const observationService = {};

/**
 * @description Observaciones de un lote de turnos. Nunca rechaza: ante un
 *              error devuelve [] para no romper la vista que las muestra.
 * @param {string[]} appointmentIds - IDs de turnos
 * @returns {Promise<Object[]>} Observaciones ({ appointment, text, ... })
 */
observationService.getByAppointments = function (appointmentIds) {
  const ids = [...new Set((appointmentIds || []).filter(Boolean))];
  if (!ids.length) return Promise.resolve([]);
  return api
    .get(endpoint, { params: { appointments: ids.join(",") }, ...SILENT })
    .then(({ data }) => (Array.isArray(data) ? data : []))
    .catch(() => []);
};

/**
 * @description Crea / edita la observación de un turno (solo staff). Texto
 *              vacío la borra.
 * @param {string} appointmentId - ID del turno
 * @param {Object} observation
 * @param {string} observation.text - Texto
 * @param {boolean} [observation.visibleToPatient] - Visible para el paciente
 * @returns {Promise<Object>} Observación guardada o `{ deleted: true }`
 */
observationService.save = function (
  appointmentId,
  { text, visibleToPatient = false }
) {
  return api
    .put(`${endpoint}/${appointmentId}`, {
      text: (text || "").trim(),
      visibleToPatient: visibleToPatient === true,
    })
    .then(({ data }) => data);
};

/**
 * @description Indexa observaciones por ID de turno.
 * @param {Object[]} observations
 * @returns {Object<string, Object>}
 */
export function indexByAppointment(observations) {
  return (observations || []).reduce((acc, o) => {
    acc[String(o.appointment)] = o;
    return acc;
  }, {});
}

/**
 * @description IDs (únicos) de los turnos tomados en una lista de slots.
 * @param {Object[]} slots - Slots con `appointment` (populado o id)
 * @returns {string[]}
 */
export function appointmentIdsOf(slots) {
  const ids = (slots || [])
    .map((s) => s && s.appointment && (s.appointment._id || s.appointment))
    .filter((id) => typeof id === "string" && id);
  return [...new Set(ids)];
}

/**
 * @description Línea de tooltip de la Agenda para una observación.
 * @param {Object|null} observation
 * @returns {string}
 */
export function observationTooltipLine(observation) {
  if (!observation || !observation.text) return "";
  const suffix = observation.visibleToPatient
    ? " (visible para el paciente)"
    : "";
  return `Obs.: ${observation.text}${suffix}`;
}

export default observationService;
